import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { MyContext } from "../../../../../MyContextApi";
// import { ToastContainer } from "react-toastify";
import ReactProSidebar from "../../../../Admin/Dashboard/ReactProSidebar";
import NavBar from "../../../../Admin/Dashboard/NavBar";
import SessionDataDrawer from "../SessionDataDrawer/SessionDataDrawer";
import FilterPopover from "../FilterPopover";
import DownloadManagerDialog from "../DownloadManagerDialog";
import Toolbar from "../Toolbar";

// ── Extracted pieces ────────────────────────────────────────────────────────
import {
  COLORS,
  CONN_UI,
  PLATFORMS,
  PLATFORM_SORT_OPTIONS,
  POST_CRAWL_REFRESH_DELAY_MS,
  TOAST_STYLE,
  TOAST_PROGRESS_STYLE,
} from "./constants";
import useSessionFetch from "./hooks/useSessionFetch";
import useSSE from "./hooks/useSSE";
import useCrawlSubmit from "./hooks/useCrawlSubmit";
import CrawlHeader from "./components/CrawlHeader";
import CrawlForm from "./components/CrawlForm";
import SessionList from "./components/SessionList";

// ─── Component ────────────────────────────────────────────────────────────────

export default function CrawlerLiveSearch({ interval }) {
  const {
    keywordInput,
    setKeywordInput,
    selectedPlatformss,
    setselectedPlatformss,
    postCount,
    setPostCount,
    settogglefilteredCrawledData,
    setIsSpecificDateEnabled,
    setDateWiseData,
    sessionData,
    dateWiseData,
    openModal,
    setOpenModal,
    statusMap,
    activeBtn,
    setActiveBtn,
    dateTimeRange,
    setdateTimeRange,
    platformWiseFilterData,
    setplatformWiseFilterData,
  } = useContext(MyContext);

  const location = useLocation();

  // ── Filter state ──────────────────────────────────────────────────────────
  const [selectedPlatform, setSelectedPlatform] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [keywordSearch, setKeywordSearch] = useState("");
  const [activeSource, setActiveSource] = useState("keyword");

  // ── Crawl-form state ──────────────────────────────────────────────────────
  const [crawlType, setCrawlType] = useState("keyword");
  const [sortOption, setSortOption] = useState("");
  const [userProfileInput, setUserProfileInput] = useState("");
  const [selectedMethod, setSelectedMethod] = useState("live");
  const [objective, setObjective] = useState("section");
  const [crawlOutputType, setCrawlOutputType] = useState("Section Analysis");

  // ── UI state ──────────────────────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);
  const [isFormValid, setIsFormValid] = useState(false);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [activeTab, setActiveTab] = useState("section");
  const [formExpanded, setFormExpanded] = useState(false);

  // ── Drawer state ──────────────────────────────────────────────────────────
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerSession, setDrawerSession] = useState(null);
  const [drawerTab, setDrawerTab] = useState("raw");
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [downloadSession, setDownloadSession] = useState(null);

  // ── Refs ───────────────────────────────────────────────────────────────────
  const crawlRefreshRef = useRef(null);
  const isFirstObjectiveRun = useRef(true);

  // ── Derived values ────────────────────────────────────────────────────────
  const availablePlatforms =
    selectedMethod === "saptang" ? ["All", ...PLATFORMS] : PLATFORMS;
  const currentSortOptions = PLATFORM_SORT_OPTIONS[selectedPlatformss] || [];

  // ── Form validation ───────────────────────────────────────────────────────
  const isPlatformSelected = selectedPlatformss.length > 0;
  const isPostCountValid =
    postCount !== "" &&
    postCount != null &&
    !isNaN(Number(postCount)) &&
    Number(postCount) >= 1 &&
    Number(postCount) <= 500;
  const isMainInputValid =
    crawlType === "keyword"
      ? keywordInput.trim() !== ""
      : userProfileInput.trim() !== "";

  useEffect(() => {
    setIsFormValid(isMainInputValid && isPlatformSelected && isPostCountValid);
  }, [isMainInputValid, isPlatformSelected, isPostCountValid]);

  // ── Write docs into the correct context slice ─────────────────────────────
  const applyDocs = useCallback(
    (docs, platform) => {
      if (!platform || platform.length === 0) {
        setplatformWiseFilterData([]);
        setDateWiseData(docs);
      } else {
        setplatformWiseFilterData(docs);
        setDateWiseData([]);
      }
    },
    [setplatformWiseFilterData, setDateWiseData],
  );

  // ── Hooks ─────────────────────────────────────────────────────────────────
  const {
    sessionsMapRef,
    crawlIsLoading,
    totalPageCount,
    hasMore,
    fetchSessionCards,
  } = useSessionFetch({ objective, applyDocs });

  const {
    connStatus,
    newIds,
    updatedIds,
    liveCount,
    openSSE,
    closeSSE,
    reconnectSSE,
    resetBadges,
  } = useSSE({
    sessionsMapRef,
    selectedPlatform,
    setDateWiseData,
    setplatformWiseFilterData,
  });

  // Wrap fetchSessionCards to also reset SSE badges
  const fetchAndResetBadges = useCallback(
    async (...args) => {
      const docs = await fetchSessionCards(...args);
      resetBadges();
      return docs;
    },
    [fetchSessionCards, resetBadges],
  );

  const scheduleCrawlRefresh = useCallback(() => {
    if (crawlRefreshRef.current) clearTimeout(crawlRefreshRef.current);
    crawlRefreshRef.current = setTimeout(() => {
      setCurrentPage(1);
      fetchAndResetBadges(1, selectedPlatform, selectedStatus);
    }, POST_CRAWL_REFRESH_DELAY_MS);
  }, [fetchAndResetBadges, selectedPlatform, selectedStatus]);

  const { isSubmitting, handleCrawlSubmit, handleRetryCrawl } = useCrawlSubmit({
    scheduleCrawlRefresh,
  });

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPageCount) return;
    setCurrentPage(newPage);
    fetchAndResetBadges(newPage, selectedPlatform, selectedStatus);
  };

  const handlePlatformClick = (platform) => {
    const next = platform === "all" ? "" : platform;
    setSelectedPlatform(next);
    setCurrentPage(1);
    settogglefilteredCrawledData(false);
    fetchAndResetBadges(1, next, selectedStatus);
  };

  const handleStatusClick = (status) => {
    const next = status === undefined ? null : status;
    setSelectedStatus(next);
    setCurrentPage(1);
    settogglefilteredCrawledData(false);
    fetchAndResetBadges(1, selectedPlatform, next);
  };

  const handleSourceClick = (source) => {
    setActiveSource(source);
    setCurrentPage(1);
    settogglefilteredCrawledData(false);
    fetchAndResetBadges(
      1,
      selectedPlatform,
      selectedStatus,
      dateTimeRange,
      keywordSearch,
      source,
    );
  };

  const handleDateWiseAPI = (timeRange) => {
    setCurrentPage(1);
    setdateTimeRange(timeRange);
    settogglefilteredCrawledData(false);
    setIsSpecificDateEnabled(true);
    setplatformWiseFilterData([]);
    if (interval) clearInterval(interval);
    fetchAndResetBadges(1, selectedPlatform, selectedStatus, timeRange);
  };

  const handleKeywordSearch = (val) => {
    setKeywordSearch(val);
    setCurrentPage(1);
    fetchAndResetBadges(
      1,
      selectedPlatform,
      selectedStatus,
      dateTimeRange,
      val,
    );
  };

  const handleClearFilters = () => {
    setSelectedStatus(null);
    setSelectedPlatform("");
    setKeywordSearch("");
    setIsSpecificDateEnabled(false);
    setdateTimeRange("30d");
    setActiveSource(null);
    fetchAndResetBadges(1, "", null, "30d", "");
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setObjective(tab === "section" ? "section" : "sentiment");
  };

  const handleCrawlTypeChange = (type) => {
    setCrawlType(type);
    setSortOption("");
    setUserProfileInput("");
    setKeywordInput("");
  };

  const handlePlatformChangeWrapped = (e) => {
    setselectedPlatformss(e.target.value);
    setSortOption("");
  };

  const onCrawlSubmit = () => {
    const newTab =
      crawlOutputType === "Sentiment Analysis" ? "sentiment" : "section";
    setActiveTab(newTab);
    setObjective(newTab);
    handleCrawlSubmit({
      selectedPlatformss,
      keywordInput,
      postCount,
      crawlType,
      userProfileInput,
      crawlOutputType,
      sortOption,
    });
  };

  // ── Mount effect ──────────────────────────────────────────────────────────
  useEffect(() => {
    const { autoRun, sessionPayload } = location.state ?? {};

    if (autoRun && sessionPayload) {
      const resolvedPlatform = Array.isArray(sessionPayload.platform)
        ? sessionPayload.platform[0]
        : sessionPayload.platform;
      handleCrawlSubmit({
        overrides: {
          keyword: sessionPayload.keyword,
          platform: resolvedPlatform,
          postCount: sessionPayload.postCount,
          type: sessionPayload.type,
          filter: sessionPayload.filter,
        },
        selectedPlatformss,
        keywordInput,
        postCount,
        crawlType,
        userProfileInput,
        crawlOutputType,
        sortOption,
      });
      window.history.replaceState({}, "");
    } else {
      fetchAndResetBadges(1, selectedPlatform, selectedStatus);
    }

    openSSE();

    return () => {
      closeSSE();
      if (crawlRefreshRef.current) clearTimeout(crawlRefreshRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch when objective changes
  useEffect(() => {
    if (isFirstObjectiveRun.current) {
      isFirstObjectiveRun.current = false;
      return;
    }
    setCurrentPage(1);
    fetchAndResetBadges(1, selectedPlatform, selectedStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objective]);

  useEffect(() => {
    if (selectedMethod === "saptang") setselectedPlatformss("All");
  }, [selectedMethod]);

  useEffect(() => {
    if (selectedPlatformss === "YouTube") setSortOption("Top");
    else if (selectedPlatformss === "Twitter") setSortOption("Latest");
    else setSortOption("");
  }, [selectedPlatformss]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const connUI = CONN_UI[connStatus] || CONN_UI.idle;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="container">
      <NavBar />

      <main
        style={{ padding: "1rem", paddingTop: "8px" }}
        className="social-media-crawler-main"
      >
        <DownloadManagerDialog
          open={downloadDialogOpen}
          onClose={() => setDownloadDialogOpen(false)}
          session={downloadSession}
          onDownload={({ reportType, format }) => {
            // handle download logic here
          }}
          currentTab={activeTab}
        />

        <div className="c-search-panel">
          {/* <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            pauseOnHover
            draggable={false}
            toastStyle={TOAST_STYLE}
            progressStyle={TOAST_PROGRESS_STYLE}
          /> */}

          <CrawlHeader
            formExpanded={formExpanded}
            onToggle={() => setFormExpanded((prev) => !prev)}
          />

          {formExpanded && (
            <CrawlForm
              crawlType={crawlType}
              onCrawlTypeChange={handleCrawlTypeChange}
              keywordInput={keywordInput}
              onKeywordChange={setKeywordInput}
              userProfileInput={userProfileInput}
              onUserProfileChange={setUserProfileInput}
              selectedPlatform={selectedPlatformss}
              onPlatformChange={handlePlatformChangeWrapped}
              availablePlatforms={availablePlatforms}
              sortOption={sortOption}
              onSortChange={setSortOption}
              currentSortOptions={currentSortOptions}
              postCount={postCount}
              setPostCount={setPostCount}
              crawlOutputType={crawlOutputType}
              onOutputTypeChange={setCrawlOutputType}
              isFormValid={isFormValid}
              isSubmitting={isSubmitting}
              onSubmit={onCrawlSubmit}
            />
          )}

          <div className="c-search-form-buttons">
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "1rem",
                marginBottom: "10px",
              }}
            >
              <FilterPopover
                anchorEl={filterAnchorEl}
                onClose={() => setFilterAnchorEl(null)}
                activeBtn={dateTimeRange}
                handleDateWiseAPI={handleDateWiseAPI}
                selectedPlatform={selectedPlatform}
                handlePlatformClick={handlePlatformClick}
                selectedStatus={selectedStatus}
                handleStatusClick={handleStatusClick}
                setSelectedStatus={setSelectedStatus}
                activeSource={activeSource}
                handleSourceClick={handleSourceClick}
                handleClearFilters={handleClearFilters}
              />
            </div>
          </div>
        </div>

        <SessionDataDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          session={drawerSession}
          initialTab={drawerTab}
        />

        <Toolbar
          activeTab={activeTab}
          onTabChange={handleTabChange}
          showSuccessful={selectedStatus === "0,1,2,3,4"}
          showFailed={selectedStatus === "-2,-3"}
          onToggleSuccessful={() => {
            const next = selectedStatus === "0,1,2,3,4" ? null : "0,1,2,3,4";
            handleStatusClick(next);
          }}
          onToggleFailed={() => {
            const next = selectedStatus === "-2,-3" ? null : "-2,-3";
            handleStatusClick(next);
          }}
          onFiltersClick={(e) =>
            setFilterAnchorEl((prev) => (prev ? null : e.currentTarget))
          }
          currentPage={currentPage}
          totalPageCount={totalPageCount}
          handlePageChange={handlePageChange}
          onKeywordSearch={handleKeywordSearch}
          searchKeyword={keywordSearch}
          activeSource={activeSource}
          onSourceChange={handleSourceClick}
          liveCount={liveCount}
          connUI={connUI}
          connStatus={connStatus}
          reconnectSSE={reconnectSSE}
        />

        <SessionList
          dateWiseData={dateWiseData}
          platformWiseFilterData={platformWiseFilterData}
          selectedPlatform={selectedPlatform}
          newIds={newIds}
          updatedIds={updatedIds}
          crawlIsLoading={crawlIsLoading}
          currentPage={currentPage}
          totalPageCount={totalPageCount}
          onPageChange={handlePageChange}
          onFetchCards={() =>
            fetchAndResetBadges(currentPage, selectedPlatform, selectedStatus)
          }
          onRetryCrawl={handleRetryCrawl}
          onViewRaw={(session) => {
            setDrawerSession(session);
            setDrawerTab("raw");
            setDrawerOpen(true);
          }}
          onViewProcessed={(session) => {
            setDrawerSession(session);
            setDrawerTab("processed");
            setDrawerOpen(true);
          }}
          onDownloadClick={(session) => {
            setDownloadSession(session);
            setDownloadDialogOpen(true);
          }}
        />
      </main>

      <ReactProSidebar />
    </div>
  );
}
